/**
 * BLRRD View Transform — ACEScg Float32 → display-encoded Uint8.
 *
 * Default tone is **neutral** (Phase 2.D, 2026-05-07). The pipeline:
 *
 *   ACEScg              → ODT matrix (ACEScg → output-gamut linear)
 *   output linear       → encode transfer (sRGB / Display-P3 sRGB-curve)
 *   encoded [0,1]       → softClip-knee + uint8 quantize
 *
 * For sRGB-bound input at identity sliders the displayed image is byte-
 * near-equal to the source (Float32 / ACEScct roundtrip wobble < 2 LSB).
 * Highlights >1.0 in scene-linear hard-clip to display white — fine for
 * sRGB photo workflows, problematic for HDR-fused brackets.
 *
 * Opt-in `tone: 'filmic'` (used by `injectFusedBracket` for HDR fusions
 * + any caller that wants the cinematic S-curve) re-introduces the
 * Narkowicz fitted polynomial before the ODT matrix:
 *
 *   ACEScg              → filmicACES (Narkowicz polynomial)
 *   tone-mapped [0,1]   → ODT matrix → encode → softClip-knee → u8
 *
 * This module is the *preview* path. The export path lives in
 * `serializers.js` and may use different operators (16-bit PNG, EXR raw).
 *
 * Codex' realistic preview limit honoured: only sRGB and Display-P3 are
 * "honest" preview targets in current browsers. Rec.2100-PQ and EXR are
 * export-only — they do NOT make sense in a Canvas2D preview.
 */

import { OUTPUT_SPACES } from '../color/spaces.js';
import {
    filmicACES, sigmoidACES,
    filmicACESPreserveHue, sigmoidACESPreserveHue,
} from '../color/aces.js';
import { softClip }      from '../color/index.js';

/* Default chrominance preservation mix. Phase 3.B-color (P3): 0.5 is
 * a middle-ground that fixes the worst Canon-RAW hue shift (especially
 * reds → orange) without flattening saturation across the board. The
 * caller can override per-render via opts.chromaPreservation. Codex
 * note: 1.0 is "correct" but desat looks weak; 0.0 is the legacy
 * (pre-P3) per-channel behaviour. */
const DEFAULT_CHROMA_PRESERVATION = 0.5;

/* Whitelist: which output spaces are valid for live preview. Everything
 * else is export-only — view-transform throws with an actionable message. */
const PREVIEW_SAFE = new Set(['sRGB', 'p3']);

/**
 * Build a fast Float32→Uint8 LUT that fuses encode + softClip-knee
 * quantize. 1024 entries cover the [0,1] post-tone-map range cleanly.
 * Cached per encode-fn since most calls share the same one (sRGB-curve).
 */
const ENCODE_LUT_CACHE = new WeakMap();
function getEncodeLUT(encodeFn) {
    let lut = ENCODE_LUT_CACHE.get(encodeFn);
    if (lut) return lut;
    lut = new Uint8Array(1024);
    for (let i = 0; i < 1024; i++) {
        const linear = i / 1023;
        const encoded = encodeFn(linear);
        lut[i] = softClip(encoded * 255);
    }
    ENCODE_LUT_CACHE.set(encodeFn, lut);
    return lut;
}

/**
 * Render an ACEScg Float32 RGBA buffer to display-encoded Uint8.
 *
 * Tone-map mode (Phase 2.D, user feedback 2026-05-06):
 *
 *   'neutral'  (default)  — skip the filmic S-curve. ACEScg values are
 *                            re-projected through the ODT matrix straight
 *                            into output gamut. For sRGB-bound inputs at
 *                            identity adjustments the displayed image is
 *                            byte-near-equal to the source (only Float32 /
 *                            ACEScg-roundtrip precision wobble, < 2 LSB).
 *                            Highlights >1.0 in scene-linear get hard-
 *                            clipped to display white — acceptable for
 *                            Lightroom-style WYSIWYG, where the user
 *                            controls highlight roll-off via curves /
 *                            highlight slider.
 *
 *   'sigmoid'             — apply darktable-style sigmoid (modified
 *                            generalized log-logistic curve, NOT a
 *                            polynomial). Tone-maps highlights without
 *                            baking the heavy midtone S-curve filmic
 *                            applies. Middle-ground option per the
 *                            Phase 1.H research roll-up. Per-channel,
 *                            opt-in. Closed-form `offset` makes
 *                            grey_point map to 0.5 (regression-guarded
 *                            by scripts/test-sigmoid-view-transform.mjs).
 *
 *   'filmic'              — apply Narkowicz filmic ACES polynomial
 *                            before the ODT matrix. Bakes the cinematic
 *                            S-curve into the preview. Use for HDR-fused
 *                            brackets, scene-linear renders, or when
 *                            the user explicitly wants the filmic look.
 *
 * Default 'neutral' fixes a user complaint that "loaded image has more
 * contrast than original" — the always-on filmic S-curve was the cause
 * (sRGB 32→19, sRGB 128→154, sRGB 255→232 at identity).
 *
 * @param {Float32Array}        srcACES    ACEScg-linear RGBA, w*h*4
 * @param {Uint8ClampedArray}   dst        destination, same length
 * @param {Uint8ClampedArray}   srcU8      original source (alpha pass-through)
 * @param {number}              w
 * @param {number}              h
 * @param {string}              outputName key into OUTPUT_SPACES
 * @param {object}              [opts]
 * @param {'neutral'|'sigmoid'|'filmic'} [opts.tone='neutral']
 * @returns {Uint8ClampedArray}
 */
export function viewTransform(srcACES, dst, srcU8, w, h, outputName = 'sRGB', opts) {
    if (!PREVIEW_SAFE.has(outputName)) {
        throw new Error(
            `viewTransform: ${outputName} is not preview-safe — export-only. ` +
            `Use sRGB or p3 for live preview.`
        );
    }
    const out = OUTPUT_SPACES[outputName];
    if (!out) throw new Error('viewTransform: unknown output space ' + outputName);

    const N = w * h * 4;
    void h;

    /* Step 1: tone-map. Default 'neutral' is a passthrough (no filmic
     * S-curve) so identity at default sliders preserves source. 'filmic'
     * applies Narkowicz fitted polynomial ACEScg → display-referred.
     * 'sigmoid' applies darktable-style log-logistic curve — middle-
     * ground tone-map between neutral and filmic (Phase 1.H).
     *
     * Phase 3.B-color (P3): both filmic and sigmoid now have a
     * chrominance-preserving variant that scales channels by f(N)/N
     * where N=max(R,G,B). Fixes Canon-RAW reds shifting to orange
     * (per-channel filmic compresses R faster than G). The mix
     * defaults to 0.5; callers can pass `chromaPreservation: 0..1`
     * (0 = legacy per-channel, 1 = full hue-preserve). */
    const tone = (opts && opts.tone) || 'neutral';
    const chromaMix = (opts && typeof opts.chromaPreservation === 'number')
        ? opts.chromaPreservation
        : DEFAULT_CHROMA_PRESERVATION;
    let toned;
    if (tone === 'filmic') {
        toned = chromaMix > 0
            ? filmicACESPreserveHue(srcACES, chromaMix)
            : filmicACES(srcACES);
    } else if (tone === 'sigmoid') {
        toned = chromaMix > 0
            ? sigmoidACESPreserveHue(srcACES, chromaMix)
            : sigmoidACES(srcACES);
    } else {
        /* neutral */
        toned = srcACES;
    }

    /* Step 2: ODT matrix ACEScg → output-gamut. */
    const m = out.matrix;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];

    /* Step 3 + 4: encode transfer + soft-clip + Uint8 quantize via LUT. */
    const encodeLUT = getEncodeLUT(out.encode);

    for (let i = 0; i < N; i += 4) {
        const tr = toned[i], tg = toned[i + 1], tb = toned[i + 2];
        const or = m0 * tr + m1 * tg + m2 * tb;
        const og = m3 * tr + m4 * tg + m5 * tb;
        const ob = m6 * tr + m7 * tg + m8 * tb;

        /* Quantize to 1024-entry LUT index, clamped. */
        const ir = or <= 0 ? 0 : or >= 1 ? 1023 : (or * 1023) | 0;
        const ig = og <= 0 ? 0 : og >= 1 ? 1023 : (og * 1023) | 0;
        const ib = ob <= 0 ? 0 : ob >= 1 ? 1023 : (ob * 1023) | 0;

        dst[i]     = encodeLUT[ir];
        dst[i + 1] = encodeLUT[ig];
        dst[i + 2] = encodeLUT[ib];
        dst[i + 3] = srcU8[i + 3];
    }
    return dst;
}

/**
 * Detect-only variant: return clip flags per pixel without writing to dst.
 * Used for the highlight/shadow-clipping overlay so the editor can show
 * which pixels go above 1 (post-tone-map) or below 0 in the *output gamut*.
 *
 * Out-of-gamut detection happens AFTER the ODT matrix — this is the right
 * place to flag "this pixel can't be shown on this display".
 */
export function detectClip(srcACES, w, h, outputName, opts) {
    const out = OUTPUT_SPACES[outputName];
    if (!out) return null;
    const wantHi = !!opts?.wantHi, wantLo = !!opts?.wantLo;
    const mask = new Uint8Array(w * h);
    const m = out.matrix;
    const m0 = m[0], m1 = m[1], m2 = m[2];
    const m3 = m[3], m4 = m[4], m5 = m[5];
    const m6 = m[6], m7 = m[7], m8 = m[8];
    /* Clip detection runs on PRE-tone-map values to flag scene clipping —
     * highlights >1 in scene-linear ACEScg are "clipped to display white"
     * after the view transform. */
    void srcACES; void m0; void m1; void m2; void m3; void m4; void m5; void m6; void m7; void m8;
    /* Skeleton — full detect implementation lands with the per-frame
     * pipeline integration. */
    void wantHi; void wantLo;
    return mask;
}
